import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

// Fixed lead-source entries beyond the individual sales executives (per LMSW list).
const FIXED_LEAD_SOURCES = ["CEO Office", "Referral", "Existing Customer", "Event", "Exhibition", "Marketing"];

const REQUIRED_FIELDS = [
  ["company_name", "Company name"],
  ["category", "Category"],
  ["lead_source", "Lead source"],
  ["client_poc_name", "Client POC name"],
  ["poc_designation", "POC designation"],
  ["client_poc_email", "POC email"],
  ["client_poc_number", "POC phone"],
];

export default function CreateLeadDialog({ open, onClose, onCreated }) {
  const [masters, setMasters] = useState({});
  const [salesExecs, setSalesExecs] = useState([]);
  const [form, setForm] = useState({
    company_name: "",
    website_url: "",
    client_poc_name: "",
    client_poc_email: "",
    client_poc_number: "",
    poc_designation: "",
    category: "",
    lead_source: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [{ data }, usersRes] = await Promise.all([
        api.get("/masters?active_only=true"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      const grouped = {};
      data.forEach((m) => {
        grouped[m.type] = grouped[m.type] || [];
        grouped[m.type].push(m);
      });
      setMasters(grouped);
      setSalesExecs(
        (usersRes.data || []).filter((u) => {
          if (u.active === false) return false;
          const role = String(u.role || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
          return role === "sales_exec" || role === "sales_manager";
        })
      );
    })();
  }, [open]);

  if (!open) return null;

  const leadSourceOptions = [
    ...new Set([
      ...(masters.lead_source || []).map((m) => m.value),
      ...salesExecs.map((u) => u.name).filter(Boolean),
      ...FIXED_LEAD_SOURCES,
      "Other",
    ]),
  ];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const categoryOtherNeedsRemark = form.category === "Others" && !form.remarks.trim();
  const sourceOtherNeedsRemark = form.lead_source === "Other" && !form.remarks.trim();

  const submit = async (e) => {
    e.preventDefault();
    const missing = REQUIRED_FIELDS.filter(([k]) => !String(form[k] || "").trim());
    if (missing.length > 0) {
      toast.error(`Required: ${missing.map(([, label]) => label).join(", ")}`);
      return;
    }
    if (categoryOtherNeedsRemark || sourceOtherNeedsRemark) {
      toast.error("Remarks is required when Category/Lead source is set to Other");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leads", form);
      toast.success("Lead created");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-lead-dialog"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Create new lead</h3>
            <p className="text-xs text-slate-500 mt-0.5">Captures the minimum required for stage “New”.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Company name *" testid="field-company-name">
            <input
              value={form.company_name}
              onChange={(e) => update("company_name", e.target.value)}
              required
              className="input"
            />
          </Field>
          <Field label="Website URL" testid="field-website-url">
            <input value={form.website_url} onChange={(e) => update("website_url", e.target.value)} placeholder="https://" className="input" />
          </Field>
          <Field label="Client POC name *" testid="field-poc-name">
            <input value={form.client_poc_name} onChange={(e) => update("client_poc_name", e.target.value)} required className="input" />
          </Field>
          <Field label="POC designation *" testid="field-poc-designation">
            <input value={form.poc_designation} onChange={(e) => update("poc_designation", e.target.value)} required className="input" />
          </Field>
          <Field label="POC email *" testid="field-poc-email">
            <input type="email" value={form.client_poc_email} onChange={(e) => update("client_poc_email", e.target.value)} required className="input" />
          </Field>
          <Field label="POC phone *" testid="field-poc-phone">
            <input value={form.client_poc_number} onChange={(e) => update("client_poc_number", e.target.value)} required className="input" />
          </Field>
          <Field label="Category *" testid="field-category">
            <select value={form.category} onChange={(e) => update("category", e.target.value)} required className="input">
              <option value="">Select…</option>
              {(masters.category || []).map((m) => <option key={m.id} value={m.value}>{m.value}</option>)}
            </select>
          </Field>
          <Field label="Lead source *" testid="field-lead-source">
            <select value={form.lead_source} onChange={(e) => update("lead_source", e.target.value)} required className="input">
              <option value="">Select…</option>
              {leadSourceOptions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label={categoryOtherNeedsRemark || sourceOtherNeedsRemark ? "Remarks *" : "Remarks"} full testid="field-remarks">
            <textarea
              rows={3}
              value={form.remarks}
              onChange={(e) => update("remarks", e.target.value)}
              placeholder={
                form.category === "Others" || form.lead_source === "Other"
                  ? "Required — describe the 'Other' category/lead source"
                  : ""
              }
              className="input"
            />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="create-lead-submit"
            disabled={saving}
            className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-medium"
          >
            {saving ? "Creating…" : "Create lead"}
          </button>
        </div>
      </form>

      <style>{`
        .input { width:100%; padding: 0.55rem 0.7rem; border: 1px solid rgb(226 232 240); border-radius: 0.375rem; font-size: 0.875rem; outline: none; background: white; }
        .input:focus { border-color: rgb(249 115 22); box-shadow: 0 0 0 2px rgba(249,115,22,0.2); }
      `}</style>
    </div>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
